import { GIVEAWAYS_GRANT, LEVELING_GRANT, NO_ENTITLEMENT } from "./entitlement.ts";

export const PERMISSION_CATALOG = [
  "team.access.read",
  "team.access.write",
  "team.settings.read",
  "team.settings.write",
  "team.settings.server.read",
  "team.settings.server.write",
  "team.settings.data.read",
  "team.settings.data.write",
  "team.billing.read",
  "team.views.share",
  "team.credentials.write",
  "team.lifecycle.write",
  "team.audit.read",
  "product.analytics.growth.read",
  "product.analytics.engagement.read",
  "product.analytics.audience.read",
  "product.analytics.profiles.read",
  "product.audit.read",
  "product.backfill.read",
  "product.backfill.write",
  "product.annotations.write",
  "product.pulse.read",
  "product.leveling.write",
  "team.billing.write",
  "team.bot.write",
  "team.lifecycle.delete",
  "team.settings.data.purge",
  "product.leveling.read",
  "product.leveling.config.write",
  "product.leveling.imports.read",
  "product.leveling.imports.write",
  "product.leveling.adjust.write",
  "product.leveling.claims.read",
  "product.leveling.claims.write",
  "product.leveling.pictures.read",
  "product.leveling.pictures.write",
  "product.leveling.member-roles.read",
  "product.leveling.member-roles.write",
  "product.images.read",
  "product.images.write",
  "product.giveaways.read",
  "product.giveaways.write",
] as const;

export type PermissionKey = (typeof PERMISSION_CATALOG)[number];

const CATALOG: ReadonlySet<string> = new Set(PERMISSION_CATALOG);

export const isPermissionKey = (value: string): value is PermissionKey => CATALOG.has(value);

export const ANALYTICS_ZONES = ["growth", "engagement", "audience", "profiles"] as const;

export type AnalyticsZone = (typeof ANALYTICS_ZONES)[number];

export const analyticsRead = (zone: AnalyticsZone): PermissionKey =>
  `product.analytics.${zone}.read`;

export const ANALYTICS_READS: readonly PermissionKey[] = ANALYTICS_ZONES.map(analyticsRead);

export const PULSE_READ: PermissionKey = "product.pulse.read";

const LEVELING_WRITE: PermissionKey = "product.leveling.write";

export const BILLING_WRITE = "team.billing.write" satisfies PermissionKey;
export const BOT_WRITE = "team.bot.write" satisfies PermissionKey;
export const LIFECYCLE_DELETE = "team.lifecycle.delete" satisfies PermissionKey;
export const SETTINGS_DATA_PURGE = "team.settings.data.purge" satisfies PermissionKey;
const LEVELING_READ = "product.leveling.read" satisfies PermissionKey;
export const LEVELING_CONFIG_WRITE = "product.leveling.config.write" satisfies PermissionKey;
export const LEVELING_IMPORTS_READ = "product.leveling.imports.read" satisfies PermissionKey;
export const LEVELING_IMPORTS_WRITE = "product.leveling.imports.write" satisfies PermissionKey;
export const LEVELING_ADJUST_WRITE = "product.leveling.adjust.write" satisfies PermissionKey;
export const LEVELING_CLAIMS_READ = "product.leveling.claims.read" satisfies PermissionKey;
export const LEVELING_CLAIMS_WRITE = "product.leveling.claims.write" satisfies PermissionKey;
export const LEVELING_PICTURES_READ = "product.leveling.pictures.read" satisfies PermissionKey;
export const LEVELING_PICTURES_WRITE = "product.leveling.pictures.write" satisfies PermissionKey;
export const LEVELING_MEMBER_ROLES_READ =
  "product.leveling.member-roles.read" satisfies PermissionKey;
export const LEVELING_MEMBER_ROLES_WRITE =
  "product.leveling.member-roles.write" satisfies PermissionKey;
export const IMAGES_READ = "product.images.read" satisfies PermissionKey;
export const IMAGES_WRITE = "product.images.write" satisfies PermissionKey;
export const GIVEAWAYS_READ = "product.giveaways.read" satisfies PermissionKey;
export const GIVEAWAYS_WRITE = "product.giveaways.write" satisfies PermissionKey;

export const LEVELING_READ_KEYS: readonly PermissionKey[] = [
  LEVELING_READ,
  LEVELING_CONFIG_WRITE,
  LEVELING_IMPORTS_READ,
  LEVELING_IMPORTS_WRITE,
  LEVELING_ADJUST_WRITE,
  LEVELING_CLAIMS_READ,
  LEVELING_CLAIMS_WRITE,
  LEVELING_PICTURES_READ,
  LEVELING_PICTURES_WRITE,
  LEVELING_MEMBER_ROLES_READ,
  LEVELING_MEMBER_ROLES_WRITE,
  IMAGES_READ,
  IMAGES_WRITE,
];

const PERMISSION_GRANT: Readonly<Partial<Record<PermissionKey, string>>> = {
  [LEVELING_WRITE]: LEVELING_GRANT,
  [LEVELING_READ]: LEVELING_GRANT,
  [LEVELING_CONFIG_WRITE]: LEVELING_GRANT,
  [LEVELING_IMPORTS_READ]: LEVELING_GRANT,
  [LEVELING_IMPORTS_WRITE]: LEVELING_GRANT,
  [LEVELING_ADJUST_WRITE]: LEVELING_GRANT,
  [LEVELING_CLAIMS_READ]: LEVELING_GRANT,
  [LEVELING_CLAIMS_WRITE]: LEVELING_GRANT,
  [LEVELING_PICTURES_READ]: LEVELING_GRANT,
  [LEVELING_PICTURES_WRITE]: LEVELING_GRANT,
  [LEVELING_MEMBER_ROLES_READ]: LEVELING_GRANT,
  [LEVELING_MEMBER_ROLES_WRITE]: LEVELING_GRANT,
  [IMAGES_READ]: LEVELING_GRANT,
  [IMAGES_WRITE]: LEVELING_GRANT,
  [GIVEAWAYS_READ]: GIVEAWAYS_GRANT,
  [GIVEAWAYS_WRITE]: GIVEAWAYS_GRANT,
};

export const holdable = (key: PermissionKey, grants: readonly string[]): boolean => {
  const grant = PERMISSION_GRANT[key];
  return grant === undefined || grants.includes(grant);
};

export const SETTINGS_SERVER_READ: PermissionKey = "team.settings.server.read";

export const SETTINGS_SERVER_WRITE: PermissionKey = "team.settings.server.write";

export const SETTINGS_DATA_READ: PermissionKey = "team.settings.data.read";

export const SETTINGS_DATA_WRITE: PermissionKey = "team.settings.data.write";

export const BILLING_READ: PermissionKey = "team.billing.read";

export const VIEWS_SHARE: PermissionKey = "team.views.share";

const SUPERSEDED_PERMISSIONS: Readonly<Partial<Record<PermissionKey, readonly PermissionKey[]>>> = {
  "team.settings.read": [SETTINGS_SERVER_READ, SETTINGS_DATA_READ, BILLING_READ],
  "team.settings.write": [
    SETTINGS_SERVER_WRITE,
    SETTINGS_DATA_WRITE,
    SETTINGS_DATA_PURGE,
    VIEWS_SHARE,
  ],
  "team.lifecycle.write": [BILLING_WRITE, BOT_WRITE, LIFECYCLE_DELETE],
  [LEVELING_WRITE]: [
    LEVELING_READ,
    LEVELING_CONFIG_WRITE,
    LEVELING_IMPORTS_READ,
    LEVELING_IMPORTS_WRITE,
    LEVELING_ADJUST_WRITE,
    LEVELING_CLAIMS_READ,
    LEVELING_CLAIMS_WRITE,
    LEVELING_PICTURES_READ,
    LEVELING_PICTURES_WRITE,
    LEVELING_MEMBER_ROLES_READ,
    LEVELING_MEMBER_ROLES_WRITE,
    IMAGES_READ,
    IMAGES_WRITE,
  ],
};

const isSuperseded = (key: string): boolean =>
  isPermissionKey(key) && SUPERSEDED_PERMISSIONS[key] !== undefined;

export const OFFERED_PERMISSIONS: readonly PermissionKey[] = PERMISSION_CATALOG.filter(
  (key) => !isSuperseded(key),
);

const successorsOf = (key: string): readonly PermissionKey[] =>
  isPermissionKey(key) ? (SUPERSEDED_PERMISSIONS[key] ?? []) : [];

const inWrittenKeys = (keys: readonly PermissionKey[]): readonly PermissionKey[] =>
  keys.flatMap((key) => (isSuperseded(key) ? successorsOf(key) : [key]));

export const offeredTerms = (terms: PolicyTerms): PolicyTerms => {
  const expanded = impliedTerms(terms);
  return {
    allow: expanded.allow.filter((key) => !isSuperseded(key)),
    deny: expanded.deny.filter((key) => !isSuperseded(key)),
  };
};

export const POLICY_SUBJECT_TYPES = ["account", "discord_role"] as const;

export type PolicySubjectType = (typeof POLICY_SUBJECT_TYPES)[number];

export interface PolicyTerms {
  readonly allow: readonly string[];
  readonly deny: readonly string[];
}

const impliedTerms = (terms: PolicyTerms): PolicyTerms => {
  const named = new Set([...terms.allow, ...terms.deny]);
  const reach = (keys: readonly string[]): readonly string[] => [
    ...keys,
    ...keys.flatMap(successorsOf).filter((key) => !named.has(key)),
  ];
  return { allow: reach(terms.allow), deny: reach(terms.deny) };
};

export const PERMISSION_PRESETS = {
  viewer: inWrittenKeys([
    LEVELING_READ,
    IMAGES_READ,
    GIVEAWAYS_READ,
    "team.access.read",
    "team.settings.read",
    "product.analytics.growth.read",
    "product.analytics.engagement.read",
    "product.analytics.audience.read",
    "product.analytics.profiles.read",
    "product.audit.read",
    "product.backfill.read",
    "product.pulse.read",
  ]),
  manager: inWrittenKeys([
    "team.access.read",
    "team.settings.read",
    "team.settings.write",
    "team.audit.read",
    "product.analytics.growth.read",
    "product.analytics.engagement.read",
    "product.analytics.audience.read",
    "product.analytics.profiles.read",
    "product.audit.read",
    "product.backfill.read",
    "product.backfill.write",
    "product.annotations.write",
    "product.pulse.read",
    "product.leveling.write",
    GIVEAWAYS_READ,
    GIVEAWAYS_WRITE,
  ]),
  admin: OFFERED_PERMISSIONS,
} as const satisfies Record<string, readonly PermissionKey[]>;

export type PresetKey = keyof typeof PERMISSION_PRESETS;

export const PRESET_KEYS = Object.keys(PERMISSION_PRESETS) as readonly PresetKey[];

const sameSet = (left: ReadonlySet<string>, right: readonly string[]): boolean =>
  left.size === right.length && right.every((key) => left.has(key));

export const presetPermissions = (
  preset: PresetKey,
  grants: readonly string[],
): readonly PermissionKey[] => PERMISSION_PRESETS[preset].filter((key) => holdable(key, grants));

export const presetOf = (terms: PolicyTerms, grants: readonly string[]): PresetKey | null => {
  terms = offeredTerms(terms);
  const counted = (key: string): boolean => !isPermissionKey(key) || holdable(key, grants);
  if (terms.deny.some(counted)) return null;
  const allowed = new Set(terms.allow.filter(counted));
  return PRESET_KEYS.find((preset) => sameSet(allowed, presetPermissions(preset, grants))) ?? null;
};

export interface ResolutionInput {
  readonly owner: boolean;
  readonly rolePolicies: readonly PolicyTerms[];
  readonly accountPolicy: PolicyTerms | null;
}

const applyTier = (
  held: ReadonlySet<string>,
  policies: readonly PolicyTerms[],
): ReadonlySet<string> => {
  const next = new Set(held);
  const implied = policies.map(impliedTerms);
  for (const policy of implied) for (const key of policy.deny) next.delete(key);
  for (const policy of implied) for (const key of policy.allow) next.add(key);
  return next;
};

export const resolvePermissions = (input: ResolutionInput): ReadonlySet<PermissionKey> => {
  if (input.owner) return new Set(PERMISSION_CATALOG);
  const afterRoles = applyTier(new Set(), input.rolePolicies);
  const afterAccount = applyTier(
    afterRoles,
    input.accountPolicy === null ? [] : [input.accountPolicy],
  );
  const resolved = new Set([...afterAccount].filter(isPermissionKey));
  const named = new Set(
    [...input.rolePolicies, ...(input.accountPolicy === null ? [] : [input.accountPolicy])]
      .map(impliedTerms)
      .flatMap((terms) => [...terms.allow, ...terms.deny]),
  );
  if (ANALYTICS_READS.some((key) => resolved.has(key))) {
    for (const key of [LEVELING_READ, IMAGES_READ] as const) {
      if (!named.has(key)) resolved.add(key);
    }
  }
  return resolved;
};

const touchedKeys = (terms: PolicyTerms): readonly string[] => [
  ...new Set([...terms.allow, ...terms.deny]),
];

export const escalates = (
  held: ReadonlySet<string>,
  ...terms: readonly PolicyTerms[]
): readonly string[] =>
  [...new Set(terms.map(offeredTerms).flatMap(touchedKeys))].filter((key) => !held.has(key)).sort();

export interface Entitlement {
  readonly grants: readonly string[];
  readonly limits: Readonly<Record<string, number>>;
}

export type DashboardAccess = {
  readonly tenantId: string;
  readonly subject: string;
  readonly guildId: string | null;
  readonly permissions: readonly string[];
  readonly entitlement: Entitlement;
  readonly staff: boolean;
};

export const hasPermission = (access: DashboardAccess, key: string): boolean =>
  access.permissions.includes(key);

export const everyPermissionIn = (permissions: readonly string[]): boolean =>
  OFFERED_PERMISSIONS.every((key) => permissions.includes(key));

export const holdsEveryPermission = (access: DashboardAccess): boolean =>
  everyPermissionIn(access.permissions);

export const hasGrant = (access: DashboardAccess, key: string): boolean =>
  access.entitlement.grants.includes(key);

export const tenantWide = (access: DashboardAccess): boolean => access.staff;

export interface TeamPolicy extends PolicyTerms {
  readonly subjectType: PolicySubjectType;
  readonly subjectId: string;
}

export interface Team {
  readonly ownerUserId: string;
  readonly guildId: string | null;
}

export interface TeamAccess {
  readonly owner: boolean;
  readonly permissions: ReadonlySet<PermissionKey>;
}

export const rolesDecide = (
  team: Team,
  discordUserId: string,
  policies: readonly TeamPolicy[],
): boolean =>
  team.ownerUserId !== discordUserId &&
  team.guildId !== null &&
  policies.some((entry) => entry.subjectType === "discord_role");

export const teamAccessFor = (
  team: Team,
  discordUserId: string,
  policies: readonly TeamPolicy[],
  roleIds: readonly string[] | null,
): TeamAccess => {
  const owner = team.ownerUserId === discordUserId;
  const held = rolesDecide(team, discordUserId, policies) ? (roleIds ?? []) : [];
  const rolePolicies = policies.filter(
    (entry) => entry.subjectType === "discord_role" && held.includes(entry.subjectId),
  );
  const accountPolicy =
    policies.find(
      (entry) => entry.subjectType === "account" && entry.subjectId === discordUserId,
    ) ?? null;
  return { owner, permissions: resolvePermissions({ owner, rolePolicies, accountPolicy }) };
};

export const teamVisible = (access: TeamAccess): boolean =>
  access.owner || access.permissions.size > 0;

export type TeamRefusal = "not_found" | "forbidden";

export const teamRefusal = (
  access: TeamAccess,
  permission: PermissionKey | null,
): TeamRefusal | null => {
  if (!teamVisible(access)) return "not_found";
  if (permission !== null && !access.permissions.has(permission)) return "forbidden";
  return null;
};

const SERVING_STATUS = "running";

export interface DashboardActor {
  readonly discordUserId: string;
  readonly staff: boolean;
  readonly suspended: boolean;
}

interface DashboardTenant extends Team {
  readonly tenantId: string;
  readonly status: string;
}

export interface DashboardFacts {
  readonly actor: DashboardActor | null;
  readonly bootstrapAdminUserId: string | null;
  readonly tenant: DashboardTenant | null;
  readonly policies: readonly TeamPolicy[];
  readonly roleIds: readonly string[] | null;
  readonly entitlement: Entitlement;
}

export type DashboardAccessOutcome =
  | { readonly kind: "unauthenticated" }
  | { readonly kind: "denied" }
  | { readonly kind: "granted"; readonly access: DashboardAccess };

const DENIED: DashboardAccessOutcome = { kind: "denied" };

export const staffOf = (actor: DashboardActor, bootstrapAdminUserId: string | null): boolean =>
  actor.staff || (bootstrapAdminUserId !== null && actor.discordUserId === bootstrapAdminUserId);

export const dashboardAccessFor = (facts: DashboardFacts): DashboardAccessOutcome => {
  const { actor, tenant } = facts;
  if (actor === null) return { kind: "unauthenticated" };
  if (actor.suspended || tenant === null) return DENIED;
  const staff = staffOf(actor, facts.bootstrapAdminUserId);
  if (tenant.status !== SERVING_STATUS && !staff) return DENIED;
  const team = teamAccessFor(tenant, actor.discordUserId, facts.policies, facts.roleIds);
  if (teamVisible(team)) {
    return {
      kind: "granted",
      access: {
        subject: actor.discordUserId,
        tenantId: tenant.tenantId,
        guildId: tenant.guildId,
        permissions: [...team.permissions],
        entitlement: facts.entitlement,
        staff,
      },
    };
  }
  if (!staff) return DENIED;
  return {
    kind: "granted",
    access: {
      subject: actor.discordUserId,
      tenantId: tenant.tenantId,
      guildId: null,
      permissions: [],
      entitlement: NO_ENTITLEMENT,
      staff,
    },
  };
};
